import { useEffect, useRef } from 'react'
import { trpc } from '@/config/trpc'
import { useNavigate, useSearchParams } from 'react-router'
import { type AuthState, AUTH_STATE_STORAGE_KEY } from '@/lib/auth/facebook'
import { toast } from 'sonner'
import { safeParseRnd } from '@/lib/auth/oauth-state'
import { providerErrorReason, trackPlatformConnectFailed } from '@/lib/analytics/connectionTracking'

export function AuthFacebookPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { mutateAsync: authorize } = trpc.platform.authorize.useMutation()

  const isMutationRunning = useRef(false)

  // Get params from URL
  const state = searchParams.get('state')
  const code = searchParams.get('code')
  const error = searchParams.get('error')
  const error_description = searchParams.get('error_description')
  const error_reason = searchParams.get('error_reason')

  useEffect(() => {
    if (isMutationRunning.current) {
      console.debug('Mutation already in progress, skipping duplicate call')
      console.debug('This should only happen in development due to StrictMode')
      return
    }

    isMutationRunning.current = true

    // The state comes back through the URL, so it can be missing or garbage.
    const returnedRnd = state ? safeParseRnd(state) : null
    const storedState = JSON.parse(
      localStorage.getItem(AUTH_STATE_STORAGE_KEY) || 'null',
    ) as AuthState | null

    if (!returnedRnd || !storedState || storedState.rnd !== returnedRnd) {
      console.error(`States did not match:\n\tstored: ${storedState}\n\trecievedState: ${state}`)
      // Reset the in-flight guard, clear stored state, and surface the
      // failure to the user — otherwise the spinner spins forever.
      isMutationRunning.current = false
      localStorage.removeItem(AUTH_STATE_STORAGE_KEY)
      // Some providers send `?error=…` without echoing the state back. That is still the
      // user cancelling or the provider failing, not a forged callback.
      const reason = !state && error ? providerErrorReason(error, error_reason) : 'state_mismatch'
      trackPlatformConnectFailed('facebook', reason, error ?? undefined)
      if (reason === 'cancelled') toast('Facebook connection cancelled')
      else toast.error('Facebook authentication state mismatch. Please try again.')
      navigate('/account/social-media')
      return
    }

    const onSuccess = (accountCount: number) => {
      // Facebook connects Pages, not the person. A user with no Page admin role completes
      // OAuth fine but gets nothing to post to, so "connected" would be a lie.
      if (accountCount === 0) {
        trackPlatformConnectFailed('facebook', 'no_accounts')
        toast.error('No Facebook Pages found', {
          description: 'You need admin access to a Facebook Page to connect it.',
        })
      } else {
        toast.success('Facebook connected successfully')
      }
      navigate(storedState.referrerPath)
    }
    const onError = (errorMessage?: string) => {
      toast.error('Failed to connect Facebook', {
        description: errorMessage,
      })
      navigate(storedState.referrerPath)
    }

    new Promise<number>((resolve, reject) => {
      if (code) {
        authorize({ platform: 'facebook', code })
          .then(({ accountCount }) => resolve(accountCount))
          .catch((e) => {
            trackPlatformConnectFailed('facebook', 'authorize_failed', e)
            reject(e)
          })
      } else if (error) {
        const reason = providerErrorReason(error, error_reason)
        trackPlatformConnectFailed('facebook', reason, error)
        reject({
          message: 'Error authenticating with Facebook',
          error,
          error_description,
          cancelled: reason === 'cancelled',
        })
      } else {
        trackPlatformConnectFailed('facebook', 'missing_code')
        reject('BUG: Code and Error not provided')
      }
    })
      .then((accountCount) => onSuccess(accountCount))
      .catch((error) => {
        // Backing out of the consent screen is a choice, not a failure worth a red toast.
        if (error?.cancelled) {
          toast('Facebook connection cancelled')
          navigate(storedState.referrerPath)
          return
        }
        console.error(error)
        onError(error?.message || error?.error_description)
      })
      .finally(() => {
        localStorage.removeItem(AUTH_STATE_STORAGE_KEY)
        isMutationRunning.current = false
      })
  }, [state, code, error, error_description, error_reason, authorize, navigate])

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="border-primary/30 border-t-primary h-12 w-12 animate-spin rounded-full border-4"></div>
        <p className="text-muted-foreground">Connecting Facebook...</p>
      </div>
    </div>
  )
}
